//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import DOMPurify, { Config } from 'dompurify';

// Catalog descriptions come from client product feeds and may contain markup.
// Only simple formatting and links are kept; everything else is stripped.
const PRODUCT_DESCRIPTION_SANITIZATION_RULES: Config = {
  ALLOWED_ATTR: ['href'],
  ALLOWED_TAGS: [
    'a',
    'b',
    'br',
    'em',
    'i',
    'li',
    'ol',
    'p',
    'span',
    'strong',
    'u',
    'ul',
  ],
};

/**
 * Sanitizes a product description that may contain HTML so it can be safely
 * rendered with `dangerouslySetInnerHTML`. Scripts, styles, event handlers and
 * unsupported tags are removed, and links are forced to open in a new tab
 * without access to the opener.
 */
export const sanitizeProductDescriptionHtml = (
  description: string | undefined,
): string => {
  if (!description) {
    return '';
  }

  const sanitizedHtml = DOMPurify.sanitize(
    description,
    PRODUCT_DESCRIPTION_SANITIZATION_RULES,
  );

  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  doc.body.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return doc.body.innerHTML.trim();
};
